import {useEffect, useMemo, useRef, useState, useCallback} from 'react';
import {useLocation} from 'react-router-dom';
// import {
//   accessGroupServiceFind,
//   buildingServiceFind,
//   intercomDeviceServiceFind,
//   roomServiceFind,
//   tenantServiceFind,
// } from 'src/apis';
// import {
//   AccessGroup,
//   AccessGroups,
//   Building,
//   IntercomDevice,
//   IntercomDevices,
//   Room,
//   Tenant,
//   Tenants,
// } from 'src/apis/autoGenerate';
import {
  accessGroupServiceFind,
  buildingServiceFind,
  intercomDeviceServiceFind,
  roomServiceFind,
  tenantServiceFind,
} from 'src/apis/autoGenerate/services';
import {
  AccessGroup,
  Building,
  IntercomDevice,
  IntercomDevices,
  Room,
  Tenant,
} from 'src/apis/autoGenerate/types';
import {AutocompleteVal} from 'src/components/SearchApartment';
import {useAppSelector} from 'src/redux/hooks';
import {selectAccount} from 'src/redux/slice/accountSlice';
import {getApartmentList, selectProperty} from 'src/redux/slice/propertySlice';
import LocalDB from 'src/utils/localDB';

export function useUrlSearchParams(keys: string[]) {
  let location = useLocation();
  const params = new URLSearchParams(location.search);
  return keys.map(key => params.get(key));
}

export function useApartmentBuildingUnit() {
  const property = useAppSelector(selectProperty);
  const account = useAppSelector(selectAccount);
  const {orgId} = useOrgId();

  const [selectedApartment, setSelectedApartment] = useState<AutocompleteVal>();
  const [selectedBuilding, setSelectedBuilding] = useState<AutocompleteVal>();
  const [buildings, setBuildings] = useState<Building[] | null>();
  const [loadingBuildings, setLoadingBuildings] = useState(false);
  const [units, setUnits] = useState<Room[] | null>();
  const [loadingUnits, setLoadingUnits] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState<AutocompleteVal>();

  useEffect(() => {
    if (orgId) {
      const userId = account.userId;
      getApartmentList(orgId, userId);
    }
  }, [orgId, account.userId]);

  useEffect(() => {
    if (selectedApartment) {
      setLoadingBuildings(true);
      buildingServiceFind(undefined, undefined, [selectedApartment.value]).then(res => {
        const _buildingRes = (res as unknown as {data: {buildings: Building[]}}).data.buildings;
        if (_buildingRes) {
          setBuildings(_buildingRes);
        } else {
          setBuildings([]);
        }
        setLoadingBuildings(false);
      });
    }
  }, [selectedApartment]);

  useEffect(() => {
    if (selectedBuilding) {
      let cancelled = false;
      const fetchAllUnits = async () => {
        setLoadingUnits(true);
        try {
          const allRooms: Room[] = [];
          let pageNum = 1;
          const pageSize = 100;
          let hasMore = true;

          while (hasMore) {
            const res = await roomServiceFind(selectedBuilding.value, undefined, pageSize, pageNum);
            const rooms = (res as unknown as {data: {rooms?: Room[]}}).data.rooms || [];
            allRooms.push(...rooms);

            hasMore = rooms.length === pageSize;
            pageNum += 1;
          }

          if (!cancelled) {
            setUnits(allRooms);
          }
        } catch (error) {
          if (!cancelled) {
            setUnits([]);
          }
        } finally {
          if (!cancelled) {
            setLoadingUnits(false);
          }
        }
      };

      fetchAllUnits();

      return () => {
        cancelled = true;
      };
    }
  }, [selectedBuilding]);

  const handleSelectApartment = (e: any, val: AutocompleteVal) => {
    setSelectedApartment(val);

    // Reset building and unit
    setSelectedBuilding(null);
    setSelectedUnit(null);

    if (val) {
      // Your existing code to fetch buildings
    }
  };

  const handleSelectBuilding = (e: any, val: AutocompleteVal) => {
    if (val?.value !== selectedBuilding?.value) {
      setSelectedUnit(null);
      setUnits([]);
    }
    setSelectedBuilding(val);
  };

  const apartmentOptions = useMemo(() => {
    return property.apartmentList.length === 0
      ? []
      : property.apartmentList.map(apartment => {
          return {
            label: apartment.name,
            value: apartment.id,
          };
        });
  }, [property.apartmentList]);

  const selectedApartmentValue = useMemo(() => {
    return apartmentOptions.filter(_ => _.value === selectedApartment?.value)[0] || null;
  }, [apartmentOptions, selectedApartment?.value]);

  const buildingOptions = useMemo(() => {
    return buildings ? buildings.map(_ => ({label: _.name, value: _.id})) : [];
  }, [buildings]);

  const selectBuildingValue = useMemo(() => {
    return buildingOptions.filter(_ => _.value === selectedBuilding?.value)[0] || null;
  }, [buildingOptions, selectedBuilding?.value]);

  const unitOptions = useMemo(() => {
    return units ? units.map(_ => ({label: _.name, value: _.id})) : [];
  }, [units]);

  const selectedUnitValue = useMemo(() => {
    return unitOptions.filter(_ => _.value === selectedUnit?.value)[0] || null;
  }, [unitOptions, selectedUnit?.value]);

  const buildingSelectDisabled = useMemo(() => {
    return buildingOptions.length === 0;
  }, [buildingOptions]);

  const unitSelectDisabled = useMemo(() => {
    return unitOptions.length === 0;
  }, [unitOptions]);

  return {
    apartmentOptions,
    selectedApartment,
    setSelectedApartment,
    buildings,
    setBuildings,
    loadingBuildings,
    setLoadingBuildings,
    buildingOptions,
    selectedBuilding,
    setSelectedBuilding,
    units,
    setUnits,
    loadingUnits,
    setLoadingUnits,
    selectedUnit,
    setSelectedUnit,
    selectedApartmentValue,
    selectBuildingValue,
    selectedUnitValue,
    buildingSelectDisabled,
    unitSelectDisabled,
    unitOptions,
    handleSelectApartment,
    handleSelectBuilding,
  };
}

export function useIntercomOptions() {
  const [intercomDevices, setIntercomDevices] = useState<IntercomDevice[]>([]);
  const [loadingIntercom, setLoadingIntercom] = useState(false);

  useEffect(() => {
    setLoadingIntercom(true);
    intercomDeviceServiceFind()
      .then(res => {
        const intercomDevices = (res as unknown as IntercomDevices).intercomDevices;
        if (intercomDevices?.length) {
          setIntercomDevices(intercomDevices);
        } else {
          setIntercomDevices([]);
        }
      })
      .finally(() => {
        setLoadingIntercom(false);
      });
  }, []);

  const intercomOptions = useMemo(() => {
    return intercomDevices.map(_ => ({value: _.id, label: _.name}));
  }, [intercomDevices]);

  return {
    loadingIntercom,
    intercomDevices,
    intercomOptions,
  };
}

export function useOrgId() {
  const account = useAppSelector(selectAccount);
  const organizations = LocalDB.get(LocalDB.alias.organizations);
  let orgId = '';

  try {
    if (organizations) {
      const parsedOrganizations = JSON.parse(organizations);
      if (Array.isArray(parsedOrganizations) && parsedOrganizations.length > 0) {
        // 修复：使用 id 字段而不是 ownerId
        orgId = parsedOrganizations[0]?.id || '';
      }
    }
  } catch (error) {
    console.error('Error parsing organizations from LocalDB:', error);
  }

  return {
    orgId:
      account?.userDetail?.organizations && account?.userDetail?.organizations[0]?.id
        ? account.userDetail.organizations[0].id
        : orgId,
  };
}

export function useAccessGroups() {
  const [loading, setLoading] = useState(false);
  const [accessGroups, setAccessGroups] = useState<AccessGroup[]>([]);
  const {orgId} = useOrgId();

  const getAccessGroups = useCallback(
    async (ids: string[] = [], name?: string) => {
      setLoading(true);
      return accessGroupServiceFind(orgId, ids, name)
        .then(res => {
          // Fix response structure to match actual API response
          const accessGroups = (res as unknown as {data: {accessGroups: AccessGroup[]}}).data
            .accessGroups;

          if (accessGroups?.length) {
            setAccessGroups(accessGroups);
          } else {
            setAccessGroups([]);
          }
          return accessGroups;
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [orgId]
  );

  const accessGroupsOptions = useMemo(() => {
    return accessGroups.map(_ => ({value: _.id, label: _.name}));
  }, [accessGroups]);

  return {getAccessGroups, loading, accessGroups, accessGroupsOptions};
}

export function useTenants() {
  const [loadingTenants, setLoadingTenants] = useState(false);
  const [tenants, setTenants] = useState<Tenant[]>([]);

  const getTenants = useCallback((name?: string) => {
    setLoadingTenants(true);
    tenantServiceFind(name)
      .then(res => {
        const tenants = res.data.tenants;
        if (Array.isArray(tenants)) {
          setTenants(
            tenants.map(_ => ({
              ..._,
              firstName: _.user?.firstName,
              lastName: _.user?.lastName,
            }))
          );
        }
      })
      .finally(() => {
        setLoadingTenants(false);
      });
  }, []);

  useEffect(() => {
    getTenants();
  }, [getTenants]);

  return {
    loadingTenants,
    tenants,
    getTenants,
  };
}

export {useLicenseQuota} from './useLicenseQuota';

/**
 * Hook to check if the current user can edit (only Org Admin and Org Owner can edit)
 * Org Manager can view but not edit
 */
export function useCanEdit() {
  const account = useAppSelector(selectAccount);
  const canEdit = useMemo(() => {
    const roleName = account.role?.name ?? account.userDetail?.role?.name;
    // Only Org Admin and Org Owner can edit
    return roleName === 'Org Admin' || roleName === 'Org Owner';
  }, [account.role?.name, account.userDetail?.role?.name]);
  return canEdit;
}

export function useCanViewDevice() {
  const account = useAppSelector(selectAccount);
  return useMemo(() => {
    const roleName = account.role?.name ?? account.userDetail?.role?.name;
    return roleName === 'Org Owner' || roleName === 'Org Admin' || roleName === 'Org Manager';
  }, [account.role?.name, account.userDetail?.role?.name]);
}

interface WebSocketMessage {
  type: string;
  data?: any;
}

interface UseWebSocketReturn {
  status: 'CONNECTING' | 'OPEN' | 'CLOSED';
  lastMsg: WebSocketMessage | null;
  send: (type: string, data?: any) => void;
  isReady: boolean;
}

const MAX_RETRIES = 5;
const MAX_RETRY_DELAY = 32000; // 32 seconds

export function useWebSocket(wsBaseUrl: string, jwtToken: string): UseWebSocketReturn {
  const wsRef = useRef<WebSocket | null>(null);
  const timerId = useRef<NodeJS.Timeout | null>(null);
  const retry = useRef(0);
  const messageQueue = useRef<WebSocketMessage[]>([]);

  const [status, setStatus] = useState<'CONNECTING' | 'OPEN' | 'CLOSED'>('CLOSED');
  const [lastMsg, setLastMsg] = useState(null);

  /** 建立连接 */
  const connect = useCallback(() => {
    if (!wsBaseUrl || !jwtToken) return;

    const url = `${wsBaseUrl}?token=${encodeURIComponent(jwtToken)}`;
    const ws = new WebSocket(url);
    wsRef.current = ws;
    setStatus('CONNECTING');

    ws.onopen = () => {
      setStatus('OPEN');
      retry.current = 0;
      console.info('[WS] open');

      // Send queued messages
      while (messageQueue.current.length > 0) {
        const message = messageQueue.current.shift();
        if (message) {
          ws.send(JSON.stringify(message));
        }
      }
    };

    ws.onmessage = evt => {
      let msg;
      try {
        msg = JSON.parse(evt.data);
      } catch {
        return;
      }
      setLastMsg(msg);
    };

    ws.onclose = () => {
      setStatus('CLOSED');
      // 断线重连（最多 5 次，指数回退）
      if (retry.current < MAX_RETRIES) {
        const delay = Math.min(1000 * 2 ** retry.current, MAX_RETRY_DELAY);
        retry.current += 1;
        clearTimeout(timerId.current!);
        timerId.current = setTimeout(connect, delay);
      }
    };

    ws.onerror = err => console.error('[WS] error', err);
  }, [wsBaseUrl, jwtToken]);

  // 首次加载 & token 变化时重连
  useEffect(() => {
    connect();
    return () => {
      if (timerId.current) {
        clearTimeout(timerId.current);
      }
      wsRef.current?.close();
    };
  }, [connect]);

  /** 发送数据 */
  const send = useCallback((type: string, data = {}) => {
    const message = {type, data};
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      try {
        wsRef.current.send(JSON.stringify(message));
      } catch (error) {
        console.error('[WS] Send error:', error);
        messageQueue.current.push(message);
      }
    } else {
      console.warn('[WS] Cannot send message - connection not open');
      messageQueue.current.push(message);
    }
  }, []);

  const isReady = useMemo(() => status === 'OPEN', [status]);

  return {status, lastMsg, send, isReady};
}
